import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Box, Stack, Typography } from '@mui/material';

import { CurrencyDecimalInput, CurrencyFixedDecimalInput } from '@/shared/formatters';
import {
  LeaseEndDatePicker,
  LeaseStartDatePicker,
  InviteLeaseDetailsAddressesAutocomplete,
  InviteLeaseDetailsTextInput,
} from '../components';
import { theme } from '@theguarantors/tg-package-material-ui';
import { useInviteTracking } from '../hooks';
import { InviteLeaseDetailsSegmentEvents } from '../constants';
import type { InviteFlowMilestoneKey } from '../types';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
}

export const LeaseInformation = ({
  control,
  errors,
  watch,
  setValue,
  trigger,
  onMilestoneReached,
}: Props) => {
  const endDateManuallyEdited = useRef(false);
  const { trackInvite } = useInviteTracking();

  return (
    <>
      <Typography
        variant="h6"
        component="h2"
        fontWeight="bold !important"
        lineHeight="28px"
        mb={1}
        sx={{
          lineHeight: '28px',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
            lineHeight: '24px',
          },
        }}
      >
        Lease information
      </Typography>
      <Typography
        variant="body2"
        fontWeight="350"
        mb={4}
        letterSpacing={0}
        sx={{
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
            lineHeight: '22px',
          },
        }}
      >
        Enter the lease details below. If your applicant is paying for the entire first month
        upfront at lease signing, we don't need to cover it (optional).
      </Typography>
      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          return (
            <InviteLeaseDetailsAddressesAutocomplete
              required
              size="small"
              value={field.value?.monolith_building_id}
              onBlur={() => field.onBlur()}
              onChange={(value: any) => {
                field.onChange(value);
                if (value?.monolith_building_id) {
                  onMilestoneReached?.('hadPropertySelected');
                  trackInvite(InviteLeaseDetailsSegmentEvents.PropertySelected, {
                    property_id: value.property_id,
                  });
                }
              }}
              name={field.name}
              label="Search by address or property name"
              skeleton={{ enabled: true }}
              error={!!errors.address}
              helperText={
                errors.address?.monolith_building_id
                  ? errors.address?.monolith_building_id.message?.toString()
                  : errors.address?.message?.toString()
              }
              data-testid="address"
            />
          );
        }}
      />
      <Controller
        name="unit"
        control={control}
        render={({ field }) => (
          <InviteLeaseDetailsTextInput
            {...field}
            ref={null}
            size="small"
            id={'unit'}
            data-testid="unit"
            label="Unit number"
            required
            error={!!errors.unit}
            helperText={errors.unit?.message?.toString()}
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 4 }} mt={2}>
        <LeaseStartDatePicker
          control={control}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          endDateManuallyEdited={endDateManuallyEdited}
        />
        <LeaseEndDatePicker
          control={control}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          watch={watch}
          endDateManuallyEdited={endDateManuallyEdited}
        />
      </Stack>
      <Controller
        name="grossMonthlyRent"
        control={control}
        render={({ field }) => (
          <InviteLeaseDetailsTextInput
            {...field}
            ref={null}
            size="small"
            slotProps={{
              input: {
                inputComponent: CurrencyDecimalInput,
                disableUnderline: true,
              },
            }}
            label="$ Total monthly rent"
            id={'gross_monthly_rent'}
            data-testid="gross_monthly_rent"
            required
            error={!!errors.grossMonthlyRent}
            helperText={
              errors.grossMonthlyRent?.message?.toString() ??
              'This is the market price rent that is on the lease.'
            }
            sx={{
              mt: 2,
              mb: 0,
            }}
          />
        )}
      />
      <Stack direction="row" gap={4}>
        <Controller
          name="prepaidRent"
          control={control}
          render={({ field }) => (
            <InviteLeaseDetailsTextInput
              {...field}
              ref={null}
              size="small"
              slotProps={{
                input: {
                  inputComponent: CurrencyFixedDecimalInput,
                  disableUnderline: true,
                },
              }}
              label="$ Prepaid rent"
              id={'prepaid-rent'}
              data-testid="prepaid-rent"
              error={!!errors.prepaidRent}
              helperText={
                <>
                  <Box>{errors.prepaidRent?.message?.toString()}</Box>
                  <Typography variant="helpertext" sx={{ color: theme.palette.neutralGrey[600] }}>
                    Any rent that will be collected at lease signing.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
        <Controller
          name="freeRent"
          control={control}
          render={({ field }) => (
            <InviteLeaseDetailsTextInput
              {...field}
              ref={null}
              size="small"
              slotProps={{
                input: {
                  inputComponent: CurrencyFixedDecimalInput,
                  disableUnderline: true,
                },
              }}
              label="$ Free rent"
              id={'free-rent'}
              data-testid="free-rent"
              error={!!errors.freeRent}
              helperText={
                <>
                  <Box>{errors.freeRent?.message?.toString()}</Box>
                  <Typography variant="helpertext" sx={{ color: theme.palette.neutralGrey[600] }}>
                    Any concessions offered to the renter where they are not responsible for making
                    rent payments.
                  </Typography>
                </>
              }
              sx={{
                mt: 2,
                mb: 0,
                flex: 1,
              }}
            />
          )}
        />
      </Stack>
    </>
  );
};
